import type { Questionnaire } from '@medplum/fhirtypes'
import {
  getComputationAnswerOptions,
  getComputationQuestionnaireItem,
  VALIDATED_TOOL_SUMMARY_ITEM,
  VALIDATED_TOOL_TAG,
} from '#src/models/questionnaires/utils/index.ts'
import { QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL } from '#src/structureDefinitions.ts'

const FOOD_SECURITY_ANSWER_OPTIONS = {
  yesNo: getComputationAnswerOptions([
    { code: 'often', score: 1 },
    { code: 'sometimes', score: 1 },
    { code: 'never', score: 0 },
    { code: 'unknown', score: 0 },
  ]),
  yesNoSimple: getComputationAnswerOptions([
    { code: 'yes', score: 1 },
    { code: 'no', score: 0 },
    { code: 'unknown', score: 0 },
  ]),
  yesNoAD1: getComputationAnswerOptions([
    { code: 'threeOrMore', score: 1 },
    { code: 'zeroToTwo', score: 0 },
  ]),
}

export const foodSecurityQuestionnaire = {
  resourceType: 'Questionnaire',
  url: 'https://www.seenhealth.org/fhir/Questionnaire/food-insecurity',
  title: 'USDA Food Security Survey - Short Form',
  name: 'Food Security',
  status: 'active',
  subjectType: ['Patient'],
  meta: {
    tag: [VALIDATED_TOOL_TAG],
  },
  item: [
    {
      linkId: 'instructions',
      type: 'display',
      text: `I'm going to read you several statements that people have made about their food situation. \
For these statements, please tell me whether the statement was often true, sometimes true, \
or never true for (you/your household) in the last 30 days.`,
    },
    {
      linkId: QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
      type: 'display',
      text: 'USDA Food Security Survey Link',
      extension: [
        {
          url: QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
          valueString:
            'https://www.ers.usda.gov/topics/food-nutrition-assistance/food-security-in-the-us/survey-tools#six',
        },
      ],
    },
    {
      linkId: 'questions',
      type: 'group',
      item: [
        {
          linkId: 'HH3',
          text: "The food that (I/we) bought just didn't last, and (I/we) didn't have money to get more.",
          type: 'choice',
          answerOption: FOOD_SECURITY_ANSWER_OPTIONS.yesNo,
        },
        {
          linkId: 'HH4',
          text: "(I/we) couldn't afford to eat balanced meals.",
          type: 'choice',
          answerOption: FOOD_SECURITY_ANSWER_OPTIONS.yesNo,
        },
        {
          linkId: 'AD1',
          text: "In the last 30 days, did (you/you or other adults in your household) ever cut the size of your meals or skip meals because there wasn't enough money for food?",
          type: 'choice',
          answerOption: FOOD_SECURITY_ANSWER_OPTIONS.yesNoSimple,
        },
        {
          linkId: 'AD1a',
          text: 'How many days did this happen?',
          type: 'choice',
          answerOption: FOOD_SECURITY_ANSWER_OPTIONS.yesNoAD1,
          enableWhen: [
            {
              question: 'AD1',
              operator: '=',
              answerCoding: {
                code: 'yes',
              },
            },
          ],
        },
        {
          linkId: 'AD2',
          text: "In the last 30 days, did you ever eat less than you felt you should because there wasn't enough money for food?",
          type: 'choice',
          answerOption: FOOD_SECURITY_ANSWER_OPTIONS.yesNoSimple,
        },
        {
          linkId: 'AD3',
          text: "In the last 30 days, were you ever hungry but didn't eat because there wasn't enough money for food?",
          type: 'choice',
          answerOption: FOOD_SECURITY_ANSWER_OPTIONS.yesNoSimple,
        },
      ],
    },
    {
      linkId: 'concluding-instructions',
      text: `In all cases, explore root causes and appropriate interventions to provide the most impactful \
intervention for participant inquiries or detected food insecurity.\n\n\
If eligible, participant may be offered HDMs from contracted services.\n\n\
For eligible participants who receive HDM from a PACE service, inform them that this survey will be \
conducted every 6 months to check on their food security status.\n\n\
For non-eligible participants inquiring about HDMs, explain the purpose of the meals, that they do not \
qualify for HDM from PACE, and offer assistance in obtaining foods from other resources if desired.\n\n\
If a non-eligible participant continues to ask for this service despite ineligibility and despite offering \
other services, please consult with IDT for possible need to initiate an SDR.`,
      type: 'display',
    },
    getComputationQuestionnaireItem([
      { code: 'very-low', score: 6 },
      { code: 'low', score: 4 },
      { code: 'marginal', score: 1 },
      { code: 'high', score: 0 },
    ]),
    VALIDATED_TOOL_SUMMARY_ITEM,
  ],
} satisfies Questionnaire

export type FoodSecurityCategory =
  | 'High Food Security'
  | 'Marginal Food Security'
  | 'Low Food Security'
  | 'Very Low Food Security'

export function foodSecurityCategory(
  score: number
): FoodSecurityCategory | undefined {
  if (score === 0) {
    return 'High Food Security'
  }
  if (score === 1) {
    return 'Marginal Food Security'
  }
  if (score >= 2 && score <= 4) {
    return 'Low Food Security'
  }
  if (score >= 5 && score <= 6) {
    return 'Very Low Food Security'
  }
  return undefined
}

export type HomeDeliveredMealEligibility =
  | 'Not Eligible'
  | 'Eligible to Continue; Not to Start'
  | 'Eligible'

export function homeDeliveredMealEligibility(
  score: number
): HomeDeliveredMealEligibility | undefined {
  if (score === 0) {
    return 'Not Eligible'
  }
  if (score === 1) {
    return 'Eligible to Continue; Not to Start'
  }
  if (score >= 2 && score <= 6) {
    return 'Eligible'
  }
  return undefined
}
